import type { ComponentDefinition, FormDocument, FormParameters, FormTemplate } from '@formbird/types';
import { createSignal, onCleanup, onMount, Show } from 'solid-js';
import { Subscription } from 'rxjs';
import { sclHandlebars } from '@formbird/solid-shared';

interface PanelComponentDefinition extends ComponentDefinition {
  wrapAction?: 'open' | 'close' | string;
  wrapFullwidth?: boolean;
  wrapHtmlType?: string;
  wrapHeadingBackground?: string;
  wrapHeadingColor?: string;
  wrapBorderColor?: string;
  wrapPanelCollapsed?: boolean;
  wrapHeading?: boolean;
  wrapClass?: string;
  collapsible?: boolean;
  allowFullBarSelection?: boolean;
  allowIconSelection?: boolean;
  linkText?: string;
  linkIcon?: string;
  url?: string;
  overrideTemplate?: string;
  urlOpenIn?: string;
  html?: string;
  useFormbirdHandlebars?: boolean;
  handlebarsHelpers?: any;
}

interface PanelProps {
  document: FormDocument;
  template: FormTemplate;
  componentDefinition: PanelComponentDefinition;
  fieldValue: any;
  fieldName: string;
  previousFieldValue: any;
  key: number;
  formParameters: FormParameters;
}

export function Panel(props: PanelProps) {
  const subs = new Subscription();
  const injector = (globalThis as any).FormbirdServiceInjector;
  const changedDocumentService = injector.get('ChangedDocumentService');
  const loggedInUserService = injector.get('LoggedInUserService');

  // 'close' panels are just end-markers — the dynamic-container reads wrapAction and breaks its loop
  if (props.componentDefinition.wrapAction === 'close') {
    return <div style="display:none;" data-panel-action="close" />;
  }

  const [componentDef, setComponentDef] = createSignal<PanelComponentDefinition>(props.componentDefinition);

  const compSub = changedDocumentService
    .watchComponentDef(props.fieldName, props.template?.documentId)
    .subscribe((compDef: any) => {
      setComponentDef(prev => ({ ...prev, ...compDef }));
      if (!hasHtml()) applyDefToDOM();
    });
  subs.add(compSub);

  onCleanup(() => subs.unsubscribe());

  const panelId = props.componentDefinition.name + props.document.documentId;

  // When html property is set, render static HTML content (like sc-static-html)
  const hasHtml = () => !!componentDef().html;
  const isPanelWrap = () => componentDef().wrapHtmlType === 'panelWrap';

  const staticHtml = () => {
    const def = componentDef();
    let html = def.html ?? '';
    const useHbs = def.useFormbirdHandlebars !== false;
    if (useHbs && html) {
      sclHandlebars.initHandlebars(def.handlebarsHelpers);
      const ctx = {
        document: props.document,
        doc: props.document,
        account: loggedInUserService?.user?.account,
        tpl: props.template,
        template: props.template,
        componentDefinition: def,
      };
      html = sclHandlebars.processHandlebars(html, null, ctx) ?? html;
    }
    return `<div class="ft-static-wrap">${html}</div>`;
  };

  const isCollapsible = () => {
    const def = componentDef();
    const compName = def.componentName;
    // sc-panel-collapse, solid-panel, and solid-panel-collapse are collapsible by default
    if (
      compName === 'sc-panel-collapse' ||
      compName === 'solid-panel' ||
      compName === 'solid-panel-collapse'
    ) {
      return def.collapsible !== false;
    }
    return def.collapsible === true;
  };

  const allowFullBarSelection = () => componentDef().allowFullBarSelection !== false;
  const allowIconSelection = () => componentDef().allowIconSelection !== false;
  const isDisabled = () => componentDef().enabled === false;
  const showHeading = () => componentDef().wrapHeading !== false;

  // DOM refs — collapse/expand is done via direct DOM manipulation so the core app
  // (dynamic-container.component.ts) can also manage state without Solid overriding it.
  let panelRef: HTMLDivElement | undefined;
  let headRef: HTMLDivElement | undefined;
  let collapsableRef: HTMLDivElement | undefined;
  let triangleRef: HTMLElement | undefined;

  const collapse = (force = false) => {
    if (panelRef?.classList.contains('disabled-panel') && !force) return;
    if (collapsableRef) {
      collapsableRef.style.maxHeight = '0';
      collapsableRef.classList.add('sc-panel-body-hidden');
      collapsableRef.classList.remove('sc-panel-body-show');
    }
    headRef?.classList.add('panel-collapsed');
    triangleRef?.classList.remove('ft-triangle-top');
    triangleRef?.classList.add('ft-triangle-bottom');
  };

  const expand = (force = false) => {
    if (panelRef?.classList.contains('disabled-panel') && !force) return;
    if (collapsableRef) {
      collapsableRef.style.maxHeight = '100%';
      collapsableRef.classList.add('sc-panel-body-show');
      collapsableRef.classList.remove('sc-panel-body-hidden');
    }
    headRef?.classList.remove('panel-collapsed');
    triangleRef?.classList.remove('ft-triangle-bottom');
    triangleRef?.classList.add('ft-triangle-top');
  };

  const handleTriangleClick = (e: MouseEvent) => {
    e.stopPropagation();
    if (!allowIconSelection()) return;
    if (headRef?.classList.contains('panel-collapsed')) {
      expand(isDisabled());
    } else {
      collapse(isDisabled());
    }
    changedDocumentService.executeOnFieldChangeWithNoChanges?.({ componentDefinition: componentDef() });
  };

  const handleHeadClick = (e: MouseEvent) => {
    e.stopPropagation();
    if (!allowFullBarSelection() || !isCollapsible()) return;
    if (headRef?.classList.contains('panel-collapsed')) {
      expand(isDisabled());
    } else {
      collapse(isDisabled());
    }
    changedDocumentService.executeOnFieldChangeWithNoChanges?.({ componentDefinition: componentDef() });
  };

  // Re-apply styles and classes after a componentDef update without disturbing children
  const applyDefToDOM = () => {
    const def = componentDef();

    if (panelRef) {
      if (def.wrapBorderColor) panelRef.style.borderColor = def.wrapBorderColor;
      if (isDisabled()) {
        panelRef.classList.add('disabled-panel', 'readonly-panel');
      } else {
        panelRef.classList.remove('disabled-panel', 'readonly-panel');
      }
    }

    if (headRef) {
      if (def.wrapHeadingBackground) headRef.style.background = def.wrapHeadingBackground;
      if (def.wrapHeadingColor) headRef.style.color = def.wrapHeadingColor;
      if (def.wrapBorderColor) headRef.style.borderColor = def.wrapBorderColor;

      const shouldApplyCollapsible = allowFullBarSelection() && isCollapsible();
      headRef.classList.toggle('allow-full-bar-selection', shouldApplyCollapsible);
      headRef.classList.toggle('disallow-full-bar-selection', !shouldApplyCollapsible);
      headRef.classList.toggle('allow-icon-selection', allowIconSelection());
      headRef.classList.toggle('disallow-icon-selection', !allowIconSelection());

      const labelTitle = document.getElementById(panelId + 'PanelLabelTitle');
      if (labelTitle) labelTitle.textContent = def.label ?? '';

      const cursor = isDisabled() ? 'not-allowed' : 'pointer';
      if (triangleRef) triangleRef.style.cursor = cursor;
    }
  };

  onMount(() => {
    // Expose collapsible state on the host element for the core app
    const host = panelRef?.closest('solid-panel,solid-panel-collapse,solid-static-html') as HTMLElement | null;
    if (host) {
      host.setAttribute('is-collapsible-panel', String(isCollapsible()));
    }

    // Static HTML panels don't have a collapsable region managed by dynamic-container
    if (hasHtml()) return;

    // Set initial collapsed state. Note: dynamic-container.component.ts may also set this
    // via setPanelCollapse() after mounting, which is fine — it manipulates the same DOM refs.
    const def = componentDef();
    const startCollapsed = isCollapsible() ? (def.wrapPanelCollapsed !== false) : (def.wrapPanelCollapsed === true);
    if (startCollapsed) {
      collapse(true);
    } else {
      expand(true);
    }
  });

  const panelClass = () => [
    'panel',
    'ft-panel',
    componentDef().wrapClass || 'panel-default',
  ].join(' ');

  const headClass = () => [
    'ft-panel-heading',
    allowFullBarSelection() && isCollapsible() ? 'allow-full-bar-selection' : 'disallow-full-bar-selection',
    allowIconSelection() ? 'allow-icon-selection' : 'disallow-icon-selection',
  ].join(' ');

  const headStyle = () => {
    const def = componentDef();
    const parts: string[] = [];
    if (def.wrapHeadingBackground) parts.push(`background:${def.wrapHeadingBackground}`);
    if (def.wrapHeadingColor) parts.push(`color:${def.wrapHeadingColor}`);
    if (def.wrapBorderColor) parts.push(`border-color:${def.wrapBorderColor}`);
    return parts.join(';');
  };

  const panelStyle = () => {
    const def = componentDef();
    return def.wrapBorderColor ? `border-color:${def.wrapBorderColor}` : '';
  };

  // Static HTML with optional panel wrapping
  if (hasHtml()) {
    return (
      <Show
        when={isPanelWrap()}
        fallback={
          // nowrap (default): just render the HTML content directly
          <div innerHTML={staticHtml()} />
        }
      >
        {/* panelWrap: render HTML inside a panel structure with heading */}
        <div
          ref={panelRef}
          id={`${panelId}panel`}
          class={panelClass()}
          style={panelStyle()}
        >
          <Show when={showHeading()}>
            <div
              ref={headRef}
              id={`${panelId}head`}
              class={headClass()}
              style={headStyle()}
              onClick={handleHeadClick}
            >
              <span id={`${panelId}PanelLabelTitle`}>
                {componentDef().label ?? ''}
              </span>
              <Show when={isCollapsible()}>
                <span id={`${panelId}triangleWrapper`} style="margin-left: auto;">
                  <p
                    ref={triangleRef}
                    class="ft ft-triangle-top"
                    id={`${panelId}triangle`}
                    style={isDisabled() ? 'cursor: not-allowed;' : 'cursor: pointer;'}
                    onClick={handleTriangleClick}
                  />
                </span>
              </Show>
            </div>
          </Show>
          <div
            ref={collapsableRef}
            id={`${panelId}collapsable`}
            class="sc-panel-body ft-panel-body"
            style="padding: 15px;"
            innerHTML={staticHtml()}
          />
        </div>
      </Show>
    );
  }

  // Standard panel (open/close wrap for dynamic child components)
  return (
    <div
      ref={panelRef}
      id={`${panelId}panel`}
      class={panelClass()}
      style={panelStyle()}
    >
      <Show when={showHeading()}>
        <div
          ref={headRef}
          id={`${panelId}head`}
          class={headClass()}
          style={headStyle()}
          onClick={handleHeadClick}
        >
          <span id={`${panelId}PanelLabelTitle`}>
            {componentDef().label ?? ''}
          </span>

          <Show when={isCollapsible()}>
            <span id={`${panelId}triangleWrapper`} style="margin-left: auto;">
              <p
                ref={triangleRef}
                class="ft ft-triangle-top"
                id={`${panelId}triangle`}
                style={isDisabled() ? 'cursor: not-allowed;' : 'cursor: pointer;'}
                onClick={handleTriangleClick}
              />
            </span>
          </Show>
        </div>
      </Show>

      {/* Children are appended here imperatively by dynamic-container.component.ts */}
      <div
        ref={collapsableRef}
        id={`${panelId}collapsable`}
        class="sc-panel-body ft-panel-body"
      />
    </div>
  );
}
